import { getDocById, resolveDocId } from "./constants";
import type { ReviewComment } from "./types";

export function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatTime(timestamp: number): string {
  try {
    return new Date(timestamp).toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
}

function renderReply(reply: string): string {
  if (!reply) return "";
  const isAi = reply.startsWith("[AI] ");
  const text = isAi ? reply.slice(5) : reply;
  return `<div class="comment-reply">
    <div class="reply-label">
      <span>${isAi ? "AI 回复" : "作者回复"}</span>
      ${isAi ? '<span class="ai-badge">AI · 助理</span>' : ""}
    </div>
    <div>${escapeHtml(text)}</div>
  </div>`;
}

export function renderCommentCard(
  comment: ReviewComment,
  options: { showDoc: boolean; isAdmin: boolean },
): string {
  const done = comment.status === "done";
  const resolvedDocId = comment.doc_id === "home" ? "home" : resolveDocId(comment.doc_id);
  const doc = resolvedDocId === "home" ? undefined : getDocById(resolvedDocId);
  const canEdit = options.isAdmin || comment.owned_by_me;
  const scope = !comment.quote_text
    ? `<span class="comment-scope">${comment.doc_id === "home" ? "项目总意见" : "全文意见"}</span>`
    : "";
  const quote = comment.quote_text
    ? `<button class="comment-quote" type="button" data-action="quote" data-id="${escapeHtml(comment.id)}">${escapeHtml(comment.quote_text)}</button>`
    : "";
  const docLink = options.showDoc
    ? comment.doc_id === "home"
      ? ""
      : `<button class="comment-doc-link" type="button" data-action="goto-doc" data-doc="${escapeHtml(resolvedDocId)}">${escapeHtml(doc?.title || "未知文档")}</button>`
    : "";
  const ownerActions = canEdit
    ? `<button class="comment-action" type="button" data-action="edit" data-id="${escapeHtml(comment.id)}">
          <i data-lucide="pencil" aria-hidden="true"></i><span>编辑</span>
        </button>
        <button class="comment-action danger" type="button" data-action="delete" data-id="${escapeHtml(comment.id)}">
          <i data-lucide="trash-2" aria-hidden="true"></i><span>删除</span>
        </button>`
    : "";
  const adminActions = options.isAdmin
    ? `<button class="comment-action" type="button" data-action="reply" data-id="${escapeHtml(comment.id)}">
          <i data-lucide="reply" aria-hidden="true"></i><span>回复</span>
        </button>
        <button class="comment-action" type="button" data-action="toggle-status" data-id="${escapeHtml(comment.id)}" data-status="${comment.status}">
          <i data-lucide="${done ? "rotate-ccw" : "check"}" aria-hidden="true"></i>
          <span>${done ? "重新打开" : "标为已处理"}</span>
        </button>`
    : "";
  const actions = ownerActions || adminActions
    ? `<div class="comment-actions">
        ${ownerActions}
        ${adminActions}
      </div>`
    : "";

  return `<article class="comment-card" data-comment-id="${escapeHtml(comment.id)}">
    <div class="comment-main">
      <div class="comment-meta">
        <span class="comment-author">${escapeHtml(comment.author_name)}</span>
        <time datetime="${new Date(comment.created_at).toISOString()}">${formatTime(comment.created_at)}</time>
        ${comment.edited_at ? '<span class="edited-label">已编辑</span>' : ""}
        <span class="status-badge ${done ? "done" : "open"}">${done ? "已处理" : "待处理"}</span>
      </div>
      ${docLink}
      ${scope}
      ${quote}
      <p class="comment-body">${escapeHtml(comment.body)}</p>
      ${renderReply(comment.reply)}
    </div>
    ${actions}
  </article>`;
}
